import React, { useEffect, useMemo, useRef, useState } from "react"
import { TAttributeValue } from "types/Pages/ProductDelta"
import Search from "Components/DesignSystem/Search/Search"
import Utils from "Common/Utils"
import { SORT } from "Pages/GMS/Products/constants"
import ViewMoreButton from "Components/Common/components/ViewMoreButton"
import ChoiceChip from "Components/DesignSystem/ChoiceChip/ChoiceChip"
import Spinner from "Components/DesignSystem/Spinner/Spinner"
import debounce from "lodash/debounce"

type MyProps = {
    attribute_values: TAttributeValue[]
    handleClick: (e: React.MouseEvent<HTMLButtonElement>, id: string, attributeValueName: string) => void
    selected: string[]
    loadAttributeValues: (query: string, page: number) => Promise<void>
    attributeValueCount: number | undefined
    page: number
    setPage: (newPage: number) => void
    /** Testid for the value-search input — templated per attribute by the parent (§ 68). */
    searchTestId?: string
    /** Testid for the view-more loading spinner — templated per attribute by the parent,
     *  for the same reason as searchTestId: a static one would duplicate across attributes. */
    loadingTestId?: string
}

const AttributeValueList = ({ attribute_values, selected, handleClick, loadAttributeValues, attributeValueCount, page, setPage, searchTestId, loadingTestId }: MyProps) => {
    const [search, setSearch] = useState<string>("")
    const [searchInput, setSearchInput] = useState<string>("")
    const [loadingAttributeValues, setLoadingAttributeValues] = useState<boolean>(false)
    /* view-more's own in-flight flag — deliberately separate from loadingAttributeValues:
       search replaces the whole grid while it loads, view-more must NOT (the loaded chips
       stay put; only the button's slot shows the DS Spinner). */
    const [loadingMore, setLoadingMore] = useState<boolean>(false)

    /* 700ms = the legacy stack (CustomSearchBar 200ms → caller 500ms, sequential).
       Latest-ref keeps the memoized debounce fresh — the legacy chain froze its
       handler at first render, a documented bug shape this migration retires. */
    const latest = useRef({ loadAttributeValues, setPage });
    latest.current = { loadAttributeValues, setPage };
    const debouncedSearch = useMemo(() => debounce(async (query: string) => {
        setLoadingAttributeValues(true)
        setSearch(query)
        latest.current.setPage(1)
        await latest.current.loadAttributeValues(query, 1)
        setLoadingAttributeValues(false);
    }, 700), []);
    useEffect(() => () => { debouncedSearch.cancel(); }, [debouncedSearch]);

    const handleSearchInput = (value: string) => {
        setSearchInput(value)
        debouncedSearch(value)
    };

    const handleViewMoreButtonClick = async () => {
        const nextPage = page + 1;
        setLoadingMore(true);
        try {
            setPage(nextPage);
            await loadAttributeValues(search, nextPage);
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div >
            <div className={`tw-pt-1`}>
                <Search
                    value={searchInput}
                    onChange={handleSearchInput}
                    /* § 288 — `loadingAttributeValues` is ALREADY search-only here (the
                       comment on its declaration says so: view-more has its own
                       `loadingMore`), and the inequality is the 700ms debounce in front
                       of it — `search` is what the debounce committed. */
                    loading={loadingAttributeValues || searchInput !== search}
                    loadingLabel="Searching values"
                    placeholder="Search Attribute Values"
                    aria-label="Search attribute values"
                    autoComplete="off"
                    data-testid={searchTestId}
                />
            </div>
            <div className={`tw-flex tw-flex-wrap tw-gap-2.5 tw-py-2`}>
                {
                    loadingAttributeValues ? <div>loading...</div> : attributeValueCount === 0 ? <div>No attribute values found</div> :
                        attribute_values?.sort(Utils.sortChoices(true, SORT.ASC, (item: TAttributeValue) => item.value, () => true))
                            .map((attrval) => (
                                /* ChoiceChip (§ 67): same toggle, now announced — the old
                                   button conveyed selection by colour alone and grew a
                                   border when picked, nudging its neighbours. */
                                <ChoiceChip
                                    key={attrval.id}
                                    data-testid={`product-attribute-value-${attrval.id}`}
                                    onClick={(e) => handleClick(e, attrval.id, attrval.value)}
                                    selected={selected.includes(attrval.id)}
                                    label={attrval.value}
                                    className="tw-min-w-[50px]"
                                />
                            ))
                }
            </div>
            {loadingMore
                ? <Spinner size="sm" label="Loading more values" className="tw-my-2" data-testid={loadingTestId} />
                : <ViewMoreButton condition={
                    attributeValueCount && attribute_values?.length < attributeValueCount && !loadingAttributeValues
                } handleClick={handleViewMoreButtonClick} state={false} />}
        </div>
    )
}

export default AttributeValueList